import { useRef, useState } from 'react';
import { APP } from '../tokens';
import type { ReviewSuggestion, ReviewStats, GeneratingStep, FoundItem } from '../../../types/review';
import ReviewSummaryStrip from './ReviewSummaryStrip';
import ReviewCard from './ReviewCard';
import ReviewPanelActions from './ReviewPanelActions';
import ReviewGeneratingPanel from './ReviewGeneratingPanel';
import ReviewCompleteBlock from './ReviewCompleteBlock';

const F = APP.fontFamily;

// ─── Icons ──────────────────────────────────────

function MinimizeIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#9E9189" strokeWidth="2" style={{ flexShrink: 0 }}>
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        stroke="#B5AA9C"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M5 12h14" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 5l7 7-7 7" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Props ──────────────────────────────────────

interface ReviewPanelProps {
  suggestions: ReviewSuggestion[];
  activeSuggestionId: string | null;
  stats: ReviewStats;
  onSelectSuggestion: (id: string) => void;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
  onAcceptAll: () => void;
  onDismissAll: () => void;
  onSend: (message: string) => void;
  onClose: () => void;
  wide?: boolean;
  generating?: boolean;
  generatingSteps?: GeneratingStep[];
  foundItems?: FoundItem[];
  generatingProgress?: number;
  generatingError?: string | null;
  onRetry?: () => void;
  onRunOCR?: () => void;
  onSaveRevised?: () => void;
  onApplyRevisedToWorkspace?: () => void | Promise<void>;
  showApplyRevisedToWorkspace?: boolean;
  applyRevisedToWorkspaceDisabled?: boolean;
  applyRevisedToWorkspaceBusy?: boolean;
  onSendForSignature?: () => void;
  onExportToGmail?: () => void;
  onViewOriginal?: () => void;
  /** When true, passes clause review styling to ReviewCards */
  clauseReviewMode?: boolean;
  /** Called when user clicks "Locate" on a clause flag card */
  onLocateInDocument?: (flagId: string, quote: string, pageHint: number | null, sectionRef: string, sectionTitle: string) => void;
  /** Map of suggestion ID → page hint for clause review flags */
  pageHints?: Record<string, number | null>;
  /** Map of suggestion ID → grounding status (populated after locateFlag calls) */
  flagGroundingStatuses?: Record<string, 'exact' | 'approx' | 'missing' | 'unknown'>;
}

// ─── Component ──────────────────────────────────

export default function ReviewPanel({
  suggestions,
  activeSuggestionId,
  stats,
  onSelectSuggestion,
  onAccept,
  onDismiss,
  onAcceptAll,
  onDismissAll,
  onSend,
  onClose,
  wide,
  generating,
  generatingSteps,
  foundItems,
  generatingProgress,
  generatingError,
  onRetry,
  onRunOCR,
  onSaveRevised,
  onApplyRevisedToWorkspace,
  showApplyRevisedToWorkspace,
  applyRevisedToWorkspaceDisabled,
  applyRevisedToWorkspaceBusy,
  onSendForSignature,
  onExportToGmail,
  onViewOriginal,
  clauseReviewMode,
  onLocateInDocument,
  pageHints,
  flagGroundingStatuses,
}: ReviewPanelProps) {
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setInputValue('');
  };

  const panelWidth = wide ? 400 : 340;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: panelWidth,
        height: '100%',
        backgroundColor: APP.bgCardBody,
        borderLeft: `1px solid ${APP.border}`,
        flexShrink: 0,
        fontFamily: F,
      }}
    >
      {/* ─── Header ──────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 40,
          flexShrink: 0,
          paddingInline: 16,
          borderBottom: `1px solid ${APP.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 400, color: APP.textMuted, lineHeight: '16px' }}>Analysis</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: APP.textMuted, lineHeight: '14px' }}>›</span>
          <span style={{ fontSize: 13, fontWeight: 500, color: APP.textCardBody, lineHeight: '16px' }}>Suggested Revisions</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
          aria-label="Minimize panel"
        >
          <MinimizeIcon />
        </button>
      </div>

      {/* ─── Generating or Review Content ───── */}
      {(generating || generatingError) && generatingSteps ? (
        <ReviewGeneratingPanel
          steps={generatingSteps}
          foundItems={foundItems ?? []}
          progress={generatingProgress ?? 0}
          error={generatingError}
          onRetry={onRetry}
          onRunOCR={onRunOCR}
        />
      ) : (
        <>
          {/* ─── Summary Strip ─────────────────── */}
          <ReviewSummaryStrip stats={stats} />

          {/* ─── Cards Scroll ──────────────────── */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              paddingBlock: 12,
              paddingInline: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {suggestions.map((suggestion) => (
              <ReviewCard
                key={suggestion.id}
                suggestion={suggestion}
                isActive={activeSuggestionId === suggestion.id}
                onSelect={() => onSelectSuggestion(suggestion.id)}
                onAccept={() => onAccept(suggestion.id)}
                onDismiss={() => onDismiss(suggestion.id)}
                clauseReviewMode={clauseReviewMode}
                onLocate={clauseReviewMode ? onLocateInDocument : undefined}
                pageHint={pageHints?.[suggestion.id] ?? null}
                groundingStatus={flagGroundingStatuses?.[suggestion.id] ?? null}
              />
            ))}
          </div>

          {/* ─── Panel Actions ─────────────────── */}
          <ReviewPanelActions
            onAcceptAll={onAcceptAll}
            onDismissAll={onDismissAll}
            hasRemaining={stats.remaining > 0}
          />
        </>
      )}

      {/* ─── Bottom: Complete Block or Input Bar ── */}
      {!generating && !generatingError && stats.total > 0 && stats.remaining === 0 && onSaveRevised ? (
        <ReviewCompleteBlock
          onSaveRevised={onSaveRevised}
          onApplyToWorkspace={onApplyRevisedToWorkspace}
          showApplyToWorkspace={showApplyRevisedToWorkspace}
          applyToWorkspaceDisabled={applyRevisedToWorkspaceDisabled}
          applyToWorkspaceBusy={applyRevisedToWorkspaceBusy}
          onSendForSignature={onSendForSignature ?? (() => {})}
          onExportToGmail={onExportToGmail ?? (() => {})}
          onViewOriginal={onViewOriginal ?? (() => {})}
        />
      ) : (
        <div style={{ flexShrink: 0, paddingBlock: 12, paddingInline: 14 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 10,
              paddingBlock: 9,
              paddingInline: 12,
              gap: 8,
              backgroundColor: '#FFFFFF',
              border: `1px solid ${APP.border}`,
            }}
          >
            <ChatIcon />
            <input
              type="text"
              placeholder="Ask about these revisions..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              style={{
                flex: 1,
                border: 'none',
                background: 'none',
                outline: 'none',
                fontSize: 13,
                fontWeight: 400,
                color: APP.textPrimary,
                fontFamily: F,
                lineHeight: '16px',
              }}
            />
            <button
              type="button"
              className="btn-hover"
              onClick={handleSend}
              disabled={!inputValue.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 6,
                backgroundColor: inputValue.trim() ? '#D97645' : APP.border,
                border: 'none',
                cursor: inputValue.trim() ? 'pointer' : 'default',
                flexShrink: 0,
                transition: 'background-color 150ms ease',
              }}
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
